import { useEffect, useRef, useState } from 'react';

import { useMapsLibrary } from '@vis.gl/react-google-maps';

import { debounce } from '@/utils/debounce';
import { getShapefileBounds, isLocationInBoundary } from '@/utils/polygon';

export type UseAutocompleteSuggestionsReturn = {
  suggestions: google.maps.places.AutocompleteSuggestion[];
  isLoading: boolean;
  resetSession: () => void;
  filteredOutCount: number;
  totalCount: number;
};

type CacheEntry = {
  suggestions: google.maps.places.AutocompleteSuggestion[];
  filteredOutCount: number;
  totalCount: number;
  timestamp: number;
};

const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

export function useAutocompleteSuggestions(
  inputString: string,
  requestOptions: Partial<google.maps.places.AutocompleteRequest> = {},
): UseAutocompleteSuggestionsReturn {
  const placesLib = useMapsLibrary('places');

  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken>(null);
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map());
  const placeCacheRef = useRef<Map<string, { location: google.maps.LatLng; timestamp: number }>>(new Map());
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredOutCount, setFilteredOutCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const performSearchRef = useRef<(searchInput: string) => Promise<void>>(async () => {});

  performSearchRef.current = async (searchInput: string) => {
    if (!placesLib) return;

    const { AutocompleteSessionToken, AutocompleteSuggestion } = placesLib;

    const bounds = getShapefileBounds();
    const locationRestriction = new google.maps.LatLngBounds(
      new google.maps.LatLng(bounds[1], bounds[0]),
      new google.maps.LatLng(bounds[3], bounds[2]),
    );

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new AutocompleteSessionToken();
    }

    const request: google.maps.places.AutocompleteRequest = {
      ...requestOptions,
      input: searchInput,
      sessionToken: sessionTokenRef.current,
      locationRestriction,
      origin: locationRestriction.getCenter(),
    };

    const cacheKey = searchInput.toLowerCase().trim();
    const cached = cacheRef.current.get(cacheKey);
    const now = Date.now();

    if (cached && now - cached.timestamp < CACHE_EXPIRY_MS) {
      setSuggestions(cached.suggestions);
      setFilteredOutCount(cached.filteredOutCount);
      setTotalCount(cached.totalCount);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
      .then(async (res) => {
        // Filter suggestions to only include those within the shapefile bounds
        const filteredSuggestions: google.maps.places.AutocompleteSuggestion[] = [];
        let outsideBoundsCount = 0;

        for (const suggestion of res.suggestions) {
          try {
            if (suggestion.placePrediction) {
              const place = suggestion.placePrediction.toPlace();
              const placeId = suggestion.placePrediction.placeId;
              const cachedPlace = placeCacheRef.current.get(placeId);
              const now = Date.now();

              let location: google.maps.LatLng | null = null;

              if (cachedPlace && now - cachedPlace.timestamp < CACHE_EXPIRY_MS) {
                location = cachedPlace.location;
              } else {
                await place.fetchFields({ fields: ['location'] });
                if (place.location) {
                  location = place.location;
                  placeCacheRef.current.set(placeId, { location, timestamp: now });
                }
              }

              if (location) {
                const lat = location.lat();
                const lng = location.lng();
                const isInBounds = isLocationInBoundary(lat, lng);

                if (isInBounds) {
                  filteredSuggestions.push(suggestion);
                } else {
                  outsideBoundsCount++;
                  console.log('Location filtered out (outside search area):', {
                    name: suggestion.placePrediction.text,
                    mainText: suggestion.placePrediction.mainText?.text,
                    secondaryText: suggestion.placePrediction.secondaryText?.text,
                    location: { lat, lng },
                    placeId: suggestion.placePrediction.placeId,
                  });
                }
              }
            }
          } catch (error) {
            console.error('Error checking suggestion location:', error);
          }
        }

        setSuggestions(filteredSuggestions);
        setFilteredOutCount(outsideBoundsCount);
        setTotalCount(res.suggestions.length);
        setIsLoading(false);

        // Cache the results
        cacheRef.current.set(cacheKey, {
          suggestions: filteredSuggestions,
          filteredOutCount: outsideBoundsCount,
          totalCount: res.suggestions.length,
          timestamp: now,
        });
      })
      .catch((e) => {
        console.log('There was a problem making the API call:', e);
        setSuggestions([]);
        setFilteredOutCount(0);
        setTotalCount(0);
        setIsLoading(false);
      });
  };

  // Create the debounced function only once
  const debouncedPerformSearch = useRef(
    debounce((searchInput: string) => {
      performSearchRef.current?.(searchInput);
    }, 300),
  );

  useEffect(() => {
    if (inputString === '' || inputString.length < 2) {
      if (suggestions.length > 0) setSuggestions([]);
      setFilteredOutCount(0);
      setTotalCount(0);
      return;
    }

    debouncedPerformSearch.current(inputString);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placesLib, inputString]);

  return {
    suggestions,
    isLoading,
    resetSession: () => {
      sessionTokenRef.current = null;
      cacheRef.current.clear();
      placeCacheRef.current.clear();
      setSuggestions([]);
      setFilteredOutCount(0);
      setTotalCount(0);
    },
    filteredOutCount,
    totalCount,
  };
}
